import {
  ButtonOptionsV3,
  ButtonV2,
  CardRouded,
  ClockIcon,
  DetailCard,
  DetailTag,
  InputDateRHFV2,
  MyModal,
  StatusTagV2,
} from "../../Atoms";
import { StatusAgendamentoEnum } from "../../../enums/statusAgendamento";
import { TipoVeiculoEnum, getTipoVeiculoLabel } from "../../../enums/tipoVeiculo";
import { useReagendarAgendamento } from "../../../hooks/useReagendarAgendamento";
import { getDataAgendamento } from "../../../utils/getDataAgendamento";
import { getTipoAtendimentoLabel } from "../../../utils/getTipoAtendimentoLabel";
import { isPrimeiroEmplacamento } from "../../../utils/isPrimeiroEmplacamento";
import { maskCep, maskCnpj, maskCpf, maskPhone } from "../../../utils/masks";
import {
  getLocalAtendimento,
  getUnidadeResponsavel,
} from "./getScheduleDetailsInfo";
import { useFormScheduleDetailV2 } from "./useFormScheduleDetailV2";

interface IDetailFieldProps {
  label: string;
  value?: string;
  className?: string;
}

const DetailField = ({ label, value, className }: IDetailFieldProps) => (
  <div className={className}>
    <span className="block font-['Mulish'] text-xs text-[#666666]">{label}</span>
    <span className="block break-words font-['Mulish'] text-base text-[#2d2d2d]">
      {value || "---"}
    </span>
  </div>
);

interface IVehicleRowProps {
  label: string;
  value?: string;
  divider?: boolean;
}

const VehicleRow = ({ label, value, divider }: IVehicleRowProps) => (
  <div
    className={
      divider
        ? "flex items-center justify-between gap-4 border-x-0 border-b border-t-0 border-solid border-[#e5e5ea] pb-1"
        : "flex items-center justify-between gap-4"
    }
  >
    <span className="font-['Mulish'] text-base text-[#2d2d2d]">{label}</span>
    <span className="break-all text-right font-['Mulish'] text-base font-bold text-[#2d2d2d]">
      {value || "---"}
    </span>
  </div>
);

export const FormScheduleDetailV2 = () => {
  const {
    agendamento,
    modalReembolso,
    setModalReembolso,
    confirmarPagamento,
    retornarStatusIniciado,
    confirmarReembolso,
    downloadLaudo,
    acessarFatura,
    onFinalize,
  } = useFormScheduleDetailV2();
  const { canReagendar, onRescheduling } = useReagendarAgendamento(agendamento);

  const cpfCnpj =
    agendamento?.cliente?.cpfCnpj?.length > 11
      ? maskCnpj(agendamento?.cliente?.cpfCnpj)
      : maskCpf(agendamento?.cliente?.cpfCnpj);

  const dataAgendamento = getDataAgendamento(agendamento?.diaAgendado);
  const hasHorarioDefinido = !!dataAgendamento && !!agendamento?.horaAgendada;
  const unidadeResponsavel = getUnidadeResponsavel(agendamento);
  const localAtendimento = getLocalAtendimento(agendamento);
  const isDeliveryComContato = !!localAtendimento?.nome;
  const showChassi = isPrimeiroEmplacamento(agendamento);
  const tipoVeiculo = agendamento?.veiculo?.tipo as TipoVeiculoEnum;
  const anoETipoVeiculo = [
    agendamento?.veiculo?.ano,
    tipoVeiculo && getTipoVeiculoLabel(tipoVeiculo),
  ]
    .filter(Boolean)
    .join(" | ");

  return (
    <div className="flex w-full flex-col gap-4">
      <CardRouded className="flex flex-col gap-4 border border-solid border-[#adb7b5] bg-white p-6 shadow-[0px_8px_24px_0px_rgba(0,0,0,0.078)] md:border-[#adb7b5] md:p-8">
        <div className="flex flex-wrap items-center gap-2.5">
          <StatusTagV2 status={agendamento?.status} variant="detail" />
          <DetailTag
            icon={<img src="/assets/svgs/icon-place-small.svg" alt="" className="h-3 w-3" />}
          >
            {getTipoAtendimentoLabel(agendamento?.tipoAtendimento)}
          </DetailTag>
          {agendamento?.revistoria && (
            <DetailTag
              className="border-[#558fff] bg-[#558fff]/10 text-[#558fff]"
              icon={<img src="/assets/svgs/icon-sync-problem-blue.svg" alt="" className="h-3 w-3" />}
            >
              Revistoria
            </DetailTag>
          )}
        </div>

        <div className="flex flex-col gap-4">
          <h2 className="break-words font-['Mulish'] text-2xl font-bold text-[#2d2d2d]">
            {agendamento?.cliente?.nome || "---"}
          </h2>
          <p className="font-['Mulish'] text-base text-[#2d2d2d]">{cpfCnpj}</p>
          <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:gap-2.5">
            <span className="font-['Mulish'] text-base text-[#2d2d2d]">
              {maskPhone(agendamento?.cliente?.telefone) || "---"}
            </span>
            <span className="hidden h-5 w-px bg-black sm:block" />
            <span className="break-all font-['Mulish'] text-base text-[#2d2d2d]">
              {agendamento?.cliente?.email || "---"}
            </span>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2.5">
          {canReagendar && (
            <ButtonV2
              type="button"
              onClick={onRescheduling}
              className="bg-[#20332f] font-normal"
            >
              <img src="/assets/svgs/icon-sync-white.svg" alt="" className="h-3.5 w-3.5" />
              Reagendar
            </ButtonV2>
          )}

          {agendamento && (
            <ButtonOptionsV3
              agendamento={agendamento}
              handleConfirmPayment={confirmarPagamento}
              handleReturnStatus={retornarStatusIniciado}
              handlePix={acessarFatura}
              handleConfirmRefund={setModalReembolso}
              onFinalize={onFinalize}
            />
          )}

          {agendamento?.status === StatusAgendamentoEnum.FINALIZADO && (
            <ButtonV2 type="button" onClick={downloadLaudo}>
              <img src="/assets/svgs/icon-down-white.svg" alt="icone download" />
              Baixar Laudo
            </ButtonV2>
          )}
        </div>
      </CardRouded>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <DetailCard label="Data e horário" icon="/assets/svgs/icon-calendar-today-gray.svg">
          {dataAgendamento ? (
            <div className="flex flex-col gap-4">
              <div className="flex flex-col gap-1">
                <p className="font-['Mulish'] text-xl font-bold text-[#20332f]">
                  {dataAgendamento.date}
                </p>
                <p className="font-['Mulish'] text-base text-[#2d2d2d]">
                  {dataAgendamento.weekday}
                </p>
              </div>
              {hasHorarioDefinido && (
                <DetailTag
                  className="w-fit border-[#38b449] bg-[#38b449]/10 text-[#0d9934]"
                  icon={<ClockIcon className="h-3 w-3" />}
                >
                  {agendamento?.horaAgendada}
                </DetailTag>
              )}
            </div>
          ) : (
            <p className="font-['Mulish'] text-xl font-bold text-[#20332f]">Não Definido</p>
          )}
        </DetailCard>

        <DetailCard label="Unidade Responsável" icon="/assets/svgs/icon-place-small.svg">
          <div className="flex flex-col gap-4">
            <p className="font-['Mulish'] text-xl font-bold text-[#20332f]">
              {unidadeResponsavel.titulo}
            </p>
            {unidadeResponsavel.endereco.length > 0 && (
              <p className="font-['Mulish'] text-base text-[#2d2d2d]">
                {unidadeResponsavel.endereco.map((linha) => (
                  <span key={linha} className="block">
                    {linha}
                  </span>
                ))}
              </p>
            )}
          </div>
        </DetailCard>

        <DetailCard label="Veículo" icon="/assets/svgs/icon-car-gray.svg">
          <div className="flex flex-col gap-4">
            <p className="font-['Mulish'] text-xl font-bold text-[#20332f]">
              {agendamento?.veiculo?.modelo || "---"}
            </p>
            {anoETipoVeiculo && (
              <p className="font-['Mulish'] text-base text-[#2d2d2d]">{anoETipoVeiculo}</p>
            )}
            <div className="flex flex-col gap-2">
              {showChassi ? (
                <VehicleRow label="Chassi" value={agendamento?.veiculo?.chassi} />
              ) : (
                <>
                  <VehicleRow label="Placa" value={agendamento?.veiculo?.placa} divider />
                  <VehicleRow label="Renavam" value={agendamento?.veiculo?.renavam} />
                </>
              )}
            </div>
          </div>
        </DetailCard>
      </div>

      {localAtendimento && (
        <DetailCard label="Local de Atendimento" icon="/assets/svgs/icon-place-small.svg">
          {localAtendimento.titulo && (
            <p className="font-['Mulish'] text-xl font-bold text-[#20332f]">
              {localAtendimento.titulo}
            </p>
          )}

          <div className="flex flex-col gap-4">
            {isDeliveryComContato && (
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <DetailField label="Nome" value={localAtendimento.nome} />
                <DetailField label="Telefone" value={maskPhone(localAtendimento.telefone)} />
              </div>
            )}
            <DetailField label="Logradouro" value={localAtendimento.endereco.logradouro} />
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <DetailField label="Número" value={localAtendimento.endereco.numero} />
              <DetailField label="Complemento" value={localAtendimento.endereco.complemento} />
              <DetailField label="Bairro" value={localAtendimento.endereco.bairro} />
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <DetailField label="Cidade" value={localAtendimento.endereco.cidade} />
              <DetailField label="UF" value={localAtendimento.endereco.uf} />
              <DetailField label="CEP" value={maskCep(localAtendimento.endereco.cep)} />
            </div>
          </div>
        </DetailCard>
      )}

      <MyModal
        isOpen={modalReembolso?.isOPen}
        onRequestClose={() => setModalReembolso({ isOPen: false, date: null })}
      >
        <form
          onSubmit={confirmarReembolso}
          className="flex w-full max-w-[90%] flex-col gap-6 p-4 md:min-w-[420px] md:max-w-[420px] md:p-6"
        >
          <p>
            Informe a data do <strong>reembolso</strong>.
          </p>

          <InputDateRHFV2
            id="dataReembolso"
            label="Data"
            selected={modalReembolso?.date}
            onChange={(date) =>
              setModalReembolso((prev) => ({
                ...prev,
                date: date as Date,
              }))
            }
          />

          <div className="flex items-center justify-end gap-4">
            <ButtonV2
              variant="text"
              type="button"
              onClick={() => setModalReembolso({ isOPen: false, date: null })}
            >
              Cancelar
            </ButtonV2>
            <ButtonV2 type="submit">Confirmar</ButtonV2>
          </div>
        </form>
      </MyModal>
    </div>
  );
};
